// src/components/AudioFloat.jsx
import React, { useEffect, useRef, useState, useCallback } from "react";
import "./AudioFloat.css";

/**
 * Props:
 * - srcs: [{ src: '...', type: 'audio/mpeg' }, { src: '...', type: 'audio/ogg' }]
 * - startMuted?: boolean (default true)
 * - startVolume?: number 0..1 (default 0.5)
 * - startTime?: number seconds to start at (default 0)
 * - label?: string (accessible label)
 * - loop?: boolean (default true)
 * - autoAttempt?: boolean (default true)          try to autoplay on mount/when srcs change
 * - preferMutedAutoplay?: boolean (default true)  force muted=true in first autoplay attempt
 * - resumeOnInteraction?: boolean (default true)  resume if autoplay blocked
 * - unmuteOnInteraction?: boolean (default true)  unmute on first user interaction (any)
 */
export default function AudioFloat({
  srcs = [{ src: "/audio/2ndChakra.mp3", type: "audio/mpeg" }],
  startMuted = true,
  startVolume = 0.5,
  startTime = 0,
  label = "Background audio",
  loop = true,
  autoAttempt = true,
  preferMutedAutoplay = true,
  resumeOnInteraction = true,
  unmuteOnInteraction = true,
} = {}) {
  const clamp01 = (n) => Math.max(0, Math.min(1, Number.isFinite(n) ? n : 0));
  const clamp = (n, lo, hi) => Math.min(hi, Math.max(lo, n));
  const audioRef = useRef(null);

  const [muted, setMuted] = useState(!!startMuted);
  const [volume, setVolume] = useState(clamp01(startVolume));
  const [isReady, setIsReady] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showPanel, setShowPanel] = useState(false);

  const haveSeekedRef = useRef(false);
  const lastSourceKeyRef = useRef("");

  const sourceKey = srcs.map((s) => `${s.type || "audio"}:${s.src}`).join("|");

  // ---- Interaction (resume + optional unmute) ----
  const interactionHandlersRef = useRef({ attached: false });

  const detachInteractionHandlers = useCallback(() => {
    if (!interactionHandlersRef.current.attached) return;
    const off = (t, fn, opts) => window.removeEventListener(t, fn, opts);
    off("pointerdown", interactionHandlersRef.current.onAny, { passive: true });
    off("touchstart", interactionHandlersRef.current.onAny, { passive: true });
    off("click", interactionHandlersRef.current.onAny);
    off("keydown", interactionHandlersRef.current.onAny);
    off("wheel", interactionHandlersRef.current.onAny, { passive: true });
    off("scroll", interactionHandlersRef.current.onAny, { passive: true });
    interactionHandlersRef.current.attached = false;
  }, []);

  const attachInteractionHandlers = useCallback((attemptPlay) => {
    if (!resumeOnInteraction || interactionHandlersRef.current.attached) return;

    const onAny = async () => {
      const el = audioRef.current;
      try {
        // Ensure start time before play
        try { applyStartTime(); } catch {}
        // Start playback if paused
        if (el && el.paused) {
          await attemptPlay();
          setIsPlaying(true);
        }
        // Unmute on first interaction if requested
        if (unmuteOnInteraction && el) {
          if (volume === 0) {
            setVolume(0.5);
            el.volume = 0.5;
          }
          setMuted(false);
          el.muted = false;
        }
      } catch {}
      detachInteractionHandlers();
    };

    interactionHandlersRef.current.onAny = onAny;
    const on = (t, fn, opts) => window.addEventListener(t, fn, opts);
    on("pointerdown", onAny, { passive: true });
    on("touchstart", onAny, { passive: true });
    on("click", onAny);
    on("keydown", onAny);
    on("wheel", onAny, { passive: true });
    on("scroll", onAny, { passive: true });
    interactionHandlersRef.current.attached = true;
  }, [resumeOnInteraction, unmuteOnInteraction, volume, detachInteractionHandlers]);

  const attemptAutoplay = useCallback(async () => {
    const el = audioRef.current;
    if (!el) return;
    if (preferMutedAutoplay) {
      el.muted = true;
      setMuted(true);
    }
    try { applyStartTime(); } catch {}

    const tryPlay = async () => {
      const node = audioRef.current;
      if (!node) return;
      await node.play();
    };

    try {
      await tryPlay();
      setIsPlaying(true);
      // If you want to auto-unmute after successful autoplay + first interaction, the handler above will do it.
      attachInteractionHandlers(tryPlay);
    } catch {
      // Autoplay blocked — resume on first interaction (then unmute if desired)
      attachInteractionHandlers(tryPlay);
    }
  }, [attachInteractionHandlers, preferMutedAutoplay]);

  // Reset seek flag when sources or startTime change
  useEffect(() => {
    if (lastSourceKeyRef.current !== sourceKey) {
      haveSeekedRef.current = false;
      lastSourceKeyRef.current = sourceKey;
      const el = audioRef.current;
      if (el) { try { el.load(); } catch {} }
    } else {
      haveSeekedRef.current = false;
    }
  }, [sourceKey, startTime]);

  // Restore persisted settings
  useEffect(() => {
    try {
      const v = localStorage.getItem("af_volume");
      const m = localStorage.getItem("af_muted");
      if (v !== null) setVolume(clamp01(parseFloat(v)));
      if (m !== null) setMuted(m === "true");
    } catch {}
  }, [startMuted, startVolume]);

  // Apply to element
  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;
    el.muted = muted;
    el.volume = clamp01(volume);
  }, [muted, volume]);

  // Persist settings
  useEffect(() => { try { localStorage.setItem("af_volume", String(clamp01(volume))); } catch {} }, [volume]);
  useEffect(() => { try { localStorage.setItem("af_muted", String(!!muted)); } catch {} }, [muted]);

  // Seek once metadata is ready
  const applyStartTime = useCallback(() => {
    const el = audioRef.current;
    if (!el || haveSeekedRef.current) return;
    const dur = el.duration;
    const safeTarget =
      Number.isFinite(dur) && dur > 0 ? clamp(startTime, 0, Math.max(0, dur - 0.25)) : Math.max(0, startTime);
    try {
      el.currentTime = safeTarget;
      haveSeekedRef.current = true;
    } catch {}
  }, [startTime]);

  // Wiring events
  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;

    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);
    const onLoadedMetadata = () => {
      setIsReady(true);
      applyStartTime();
      if (autoAttempt) setTimeout(() => { attemptAutoplay(); }, 0);
    };
    const onCanPlay = () => {
      setIsReady(true);
      applyStartTime();
    };

    el.addEventListener("play", onPlay);
    el.addEventListener("pause", onPause);
    el.addEventListener("loadedmetadata", onLoadedMetadata);
    el.addEventListener("canplay", onCanPlay);

    return () => {
      el.removeEventListener("play", onPlay);
      el.removeEventListener("pause", onPause);
      el.removeEventListener("loadedmetadata", onLoadedMetadata);
      el.removeEventListener("canplay", onCanPlay);
    };
  }, [applyStartTime, autoAttempt, attemptAutoplay]);

  // Clean up
  useEffect(() => () => {
    detachInteractionHandlers();
  }, [detachInteractionHandlers]);

  // Manual play/pause
  const togglePlay = useCallback(async () => {
    const el = audioRef.current;
    if (!el) return;
    applyStartTime();
    if (el.paused) {
      try {
        await el.play();
        setIsPlaying(true);
        if (volume === 0 && !muted) setMuted(true);
      } catch {}
    } else {
      el.pause();
      setIsPlaying(false);
    }
  }, [muted, volume, applyStartTime]);

  // Mute/unmute
  const toggleMute = useCallback(async () => {
    const el = audioRef.current;
    if (!el) return;
    const next = !muted;
    setMuted(next);
    el.muted = next;
    if (el.paused && !next) {
      try { await el.play(); setIsPlaying(true); } catch {}
    }
  }, [muted]);

  // Icons
  const IconPlay = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true"><path d="M8 5v14l11-7z" /></svg>
  );
  const IconPause = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true"><path d="M6 5h4v14H6zM14 5h4v14h-4z" /></svg>
  );
  const IconVol = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M3 10v4h4l5 5V5L7 10H3z" />
      <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v8.06A4.51 4.51 0 0016.5 12z" />
      <path d="M14 3.23v2.06c2.89 1 5 3.77 5 6.71s-2.11 5.71-5 6.71v2.06c4.01-1.14 7-4.93 7-8.77s-2.99-7.63-7-8.77z" />
    </svg>
  );
  const IconMute = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M3 10v4h4l5 5V5L7 10H3z" />
      <path d="M16 9l5 5m0-5l-5 5" stroke="currentColor" strokeWidth="2" fill="none" />
    </svg>
  );
  const IconChevron = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true"><path d="M7 10l5 5 5-5z" /></svg>
  );

  return (
    <>
      <audio
        ref={audioRef}
        loop={loop}
        preload="metadata"
        playsInline
        autoPlay={autoAttempt}
        defaultMuted={startMuted}
        muted={muted}
      >
        {srcs.map((s) => (
          <source key={s.src} src={s.src} type={s.type} />
        ))}
        Your browser does not support the audio element.
      </audio>

      <div className="af-container" role="group" aria-label={label}>
        <button
          className="af-btn"
          type="button"
          onClick={togglePlay}
          aria-label={isPlaying ? "Pause audio" : "Play audio"}
          title={isPlaying ? "Pause" : "Play"}
        >
          {isPlaying ? <IconPause /> : <IconPlay />}
        </button>

        <button
          className="af-btn"
          type="button"
          onClick={toggleMute}
          aria-label={muted ? "Unmute" : "Mute"}
          title={muted ? "Unmute" : "Mute"}
        >
          {muted ? <IconMute /> : <IconVol />}
        </button>

        <button
          className="af-btn af-chevron hidden"
          type="button"
          onClick={() => setShowPanel((v) => !v)}
          aria-expanded={showPanel}
          aria-controls="af-volume-panel"
          title="Volume"
        >
          <IconChevron />
        </button>

        <div
          id="af-volume-panel"
          className={` af-panel ${showPanel ? "open hidden" : "hidden"}`}
          onMouseLeave={() => setShowPanel(false)}
        >
          <label className="sr-only" htmlFor="af-volume-range">Volume</label>
          <input
            id="af-volume-range"
            className="af-range"
            type="range"
            min="0"
            max="1"
            step="0.01"
            value={muted ? 0 : volume}
            onChange={(e) => {
              const v = clamp01(parseFloat(e.target.value));
              setVolume(v);
              if (v === 0 && !muted) setMuted(true);
              if (v > 0 && muted) setMuted(false);
              const el = audioRef.current;
              if (el) el.volume = v;
            }}
            orient="vertical"
            aria-valuemin={0}
            aria-valuemax={1}
            aria-valuenow={muted ? 0 : volume}
            aria-valuetext={muted ? "Muted" : `${Math.round(volume * 100)}%`}
          />
          <div className="af-volume-label">
            {muted ? "Muted" : `${Math.round(volume * 100)}%`}
          </div>
        </div>

        <div className={`af-dot ${isReady ? "ok" : ""}`} aria-hidden="true" />
      </div>
    </>
  );
}
